import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import type { CancelAndRebookPayload, CancelAndRebookResponse } from '../types';

type ServiceUserEnvelope<T> = {
  message?: string;
  status?: string;
  responseBody?: T;
  errorCode?: number;
  errorMessage?: string | null;
};

const ENDPOINT =
  '/user/v1/trainer-slots/parent-connect/request/cancel-and-rebook';

// Cancel a trainer-initiated ACTIVE parent connect AND book a fresh
// pending slot in one call. service-user cancels first (BSS) then runs
// the standard create-pending flow for the new slot.
//
// The envelope's HTTP status is 200 for BOTH a full success and a
// partial one (cancel went through but the rebook couldn't grab the
// slot); the caller branches on `responseBody.rebooked`. Only a real
// failure (bad slot pre-validation / cancel itself failing) comes back
// as a thrown error (non-2xx) - in that case nothing changed and the
// session is still active.
export const cancelAndRebookParentConnect = async (
  payload: CancelAndRebookPayload
): Promise<CancelAndRebookResponse> => {
  const data: ServiceUserEnvelope<CancelAndRebookResponse> = await axios.post(
    ENDPOINT,
    payload
  );
  return data?.responseBody ?? {};
};

export const cancelAndRebookParentConnectMockHandler = http.post(
  `${env.API_URL}${ENDPOINT}`,
  async ({ request }) => {
    await networkDelay();
    let startEpoch = Math.floor(Date.now() / 1000) + 36 * 60 * 60;
    let durationSeconds = 900;
    try {
      const body = (await request.json()) as Partial<CancelAndRebookPayload>;
      const parsedDuration = Number(body.duration_seconds);
      if (Number.isFinite(parsedDuration) && parsedDuration > 0) {
        durationSeconds = parsedDuration;
      }
      if (body.start_date && body.start_time) {
        const [yStr, mStr, dStr] = body.start_date.split('-');
        const [hhStr, mmStr] = body.start_time.split(':');
        const dt = new Date(
          Number.parseInt(yStr ?? '2026', 10),
          Number.parseInt(mStr ?? '1', 10) - 1,
          Number.parseInt(dStr ?? '1', 10),
          Number.parseInt(hhStr ?? '0', 10),
          Number.parseInt(mmStr ?? '0', 10),
          0,
          0
        );
        startEpoch = Math.floor(dt.getTime() / 1000);
      }
    } catch {
      /* swallow - dev-only mock, keep defaults */
    }
    return HttpResponse.json<ServiceUserEnvelope<CancelAndRebookResponse>>({
      status: 'SUCCESS',
      message: 'Parent-connect session cancelled and rebooked',
      errorCode: 0,
      errorMessage: null,
      responseBody: {
        cancelled: true,
        rebooked: true,
        message: 'Parent connect cancelled and rebooked',
        request: {
          session_id: 'mock-session-id',
          start_time: startEpoch,
          end_time: startEpoch + durationSeconds,
          timeZone: 'Asia/Kolkata',
        },
      },
    });
  }
);
